import { ComponentType, lazy, LazyExoticComponent } from 'react';

import Sentry from '../services/sentry';

/**
 * Stale-bundle recovery for code-split chunks.
 *
 * The app is code-split: routes and heavy widgets load via dynamic `import()`
 * (see `routes/lazyImports.tsx`, `LazyECharts`, `LazyAgGrid`). Parcel
 * content-hashes every chunk (`Home.401386b8.js`). After a new deploy, a tab
 * still running the OLD build references OLD chunk URLs; requesting one fails
 * (404, or the SPA host returns `index.html` with a `text/html` MIME type), so
 * the dynamic import rejects and dead-ends on the "Oops, that's our bad!" error
 * boundary in `routes.tsx`. `lazyWithReload` reloads the page to pull the freshly
 * deployed bundle so the user auto-recovers on the page they wanted.
 *
 * Naming: it RELOADS (it does not retry the import in place). Reloading is the
 * only thing that swaps the stale `index.html` for the fresh one.
 *
 * ── Loop safety ───────────────────────────────────────────────────────────────
 * Auto-reloads are capped by a MONOTONIC `sessionStorage` counter: only ever
 * incremented (before each reload, write verified by read-back), NEVER reset by
 * time or by a successful import. At `MAX_RELOADS` we stop and let the error
 * surface to the boundary. This bounds auto-reloads to <= MAX_RELOADS per tab,
 * unconditionally — an infinite loop is impossible by construction regardless of
 * reload-cycle duration or false positives. We do NOT re-arm the budget: every
 * re-arm we tried (on-success, or after a time window) reopened an unbounded
 * loop. `sessionStorage` is per-tab and clears on tab close, so each fresh tab
 * starts with a full budget; a single tab open across many deploys is the only
 * case that can exhaust it, and it then dead-ends gracefully (manual refresh).
 *
 * ── index.html caching ────────────────────────────────────────────────────────
 * A reload only helps if it fetches a FRESH `index.html`. We defend against a
 * browser-cached stale `index.html` by reloading to a rotating cache-bust query
 * param (a different URL → guaranteed browser-cache miss → refetch), and strip
 * the param again on the next load. The durable fix is still infra: serve
 * `index.html` with `Cache-Control: no-cache` (hashed assets stay immutable).
 */

// sessionStorage keys use the repo's hyphen convention. Kept local to this
// module (they are session-scoped recovery state, not app localStorage config).
const RELOAD_COUNT_KEY = 'calyx-stale-chunk-reloads';
const RECOVERY_MARKER_KEY = 'calyx-stale-chunk-recovery';
const CACHE_BUST_PARAM = '_sbust';
const MAX_RELOADS = 3;
// If a reload is somehow blocked (cancelled beforeunload, embedded/extension
// context), fall through to the ErrorBoundary after this delay instead of
// hanging on the Suspense spinner forever.
const RELOAD_FALLBACK_MS = 10_000;

// In-memory, resets on every real page load. Ensures at most ONE increment+reload
// per page load even when several lazy chunks reject in the same tick (a deploy
// typically invalidates many chunks at once), so the budget counts distinct
// recovery *events*, not raw chunk failures.
let isReloadInFlight = false;
let guardInstalled = false;
let exhaustionReported = false;

interface RecoveryMarker {
  attempt: number;
  reason: string;
  at: number;
}

/**
 * Best-effort heuristic for "a dynamic import failed because its chunk is no
 * longer on the server". Covers the message wording across Chrome, Firefox and
 * Safari, plus the SPA-fallback case where the missing `.js` is answered with
 * `index.html` and rejected for its MIME type. The bare "Unexpected token '<'"
 * parse symptom is deliberately EXCLUDED: it also fires on unrelated JSON/HTML
 * mis-parses, and the explicit MIME-type message below covers the real
 * module-load case on modern engines. The `ChunkLoadError` / `Loading chunk`
 * alternations are webpack terminology — inert under Parcel, kept as cheap
 * defence for any future bundler change.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? `${error.name}: ${error.message}`
        : String((error as { message?: unknown })?.message ?? '');

  return /(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to load module script|ChunkLoadError|Loading chunk [\w-]+ failed|module script but the server responded with a MIME type of)/i.test(
    message
  );
}

function readReloadCount(): number {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_COUNT_KEY);
    if (raw == null) return 0; // never set → full budget (allow the first recovery)
    const n = Number.parseInt(raw, 10);
    // Present-but-corrupt fails CLOSED (treated as exhausted) so tampering can't
    // re-arm the budget; a genuinely absent key above still allows recovery.
    return Number.isInteger(n) && n >= 0 ? n : MAX_RELOADS;
  } catch {
    // Storage unreadable → 0, but the verified write below still prevents a loop:
    // a reload only happens if the increment can be persisted AND read back.
    return 0;
  }
}

/**
 * Persist the next reload count and confirm it stuck via read-back. Returns
 * `true` only when durable — the caller relies on this to decide whether
 * reloading is safe. If storage is disabled/full/silently dropping writes this
 * returns `false` and we do NOT reload, which is the loop-safe choice.
 */
function persistReloadCount(count: number): boolean {
  try {
    const value = String(count);
    window.sessionStorage.setItem(RELOAD_COUNT_KEY, value);
    return window.sessionStorage.getItem(RELOAD_COUNT_KEY) === value;
  } catch {
    return false;
  }
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function writeRecoveryMarker(marker: RecoveryMarker): void {
  try {
    window.sessionStorage.setItem(RECOVERY_MARKER_KEY, JSON.stringify(marker));
  } catch {
    /* telemetry is best-effort; never block recovery on it */
  }
}

/** Reload to a cache-bust URL so a browser-cached index.html is refetched. */
function reloadWithCacheBust(nextCount: number): void {
  try {
    const url = new URL(window.location.href);
    // Deploy-unique token (timestamp + attempt), NOT the bare 1..MAX_RELOADS
    // counter: a plain counter reuses `?_sbust=1` across deploys, so a stale
    // `index.html?_sbust=1` cached during an earlier deploy's recovery could be
    // served again on a later deploy — defeating the bust and burning the budget.
    // A fresh token per reload guarantees a distinct URL → cache miss every time.
    url.searchParams.set(CACHE_BUST_PARAM, `${Date.now()}-${nextCount}`);
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * Trigger a bounded recovery reload for a stale-chunk error. Returns `true` if a
 * reload was initiated (caller should hold), `false` if the error is not a chunk
 * error, we are offline, the budget is exhausted, or the increment could not be
 * persisted (in which case we deliberately do NOT reload, to stay loop-safe).
 */
function reloadForStaleChunk(error: unknown): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false;

  // A reload for this page load is already scheduled — hold without spending more
  // budget (so the caller keeps the Suspense fallback / swallows the rejection).
  if (isReloadInFlight) return true;

  // Offline: a reload can't fetch a fresh bundle (or even index.html), so let the
  // recoverable error boundary show rather than burning budget on a dead reload.
  if (isOffline()) return false;

  const count = readReloadCount();
  if (count >= MAX_RELOADS) {
    // Budget spent and still failing → not a recoverable stale bundle. Emit a
    // distinct, alertable signal once, then let the error surface to the boundary.
    if (!exhaustionReported) {
      exhaustionReported = true;
      Sentry.captureMessage('Stale-bundle chunk recovery exhausted', {
        level: 'error',
        tags: { stale_chunk_recovery: 'exhausted' },
      });
    }
    return false;
  }

  // Only reload if we could record the attempt; otherwise we can't guarantee the
  // bound, so we choose the safe option (no reload) over risking a storm.
  if (!persistReloadCount(count + 1)) return false;

  writeRecoveryMarker({
    attempt: count + 1,
    reason: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
    at: Date.now(),
  });

  isReloadInFlight = true;
  reloadWithCacheBust(count + 1);
  return true;
}

/**
 * Remove the cache-bust query param from the URL after a recovery reload. MUST be
 * called before `createBrowserRouter` (see routes.tsx): the router snapshots
 * `window.location` when it is created, so stripping later would leave `?_sbust`
 * lingering in react-router's `useLocation()`/`useSearchParams()` (and in the
 * address bar / router-sourced telemetry) until the first navigation.
 */
export function absorbCacheBustParam(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(CACHE_BUST_PARAM)) {
      url.searchParams.delete(CACHE_BUST_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    /* no-op */
  }
}

/** Emit (once) the recovery signal left by a reload, so the reload doesn't hide it. */
function reportPendingRecovery(): void {
  try {
    const raw = window.sessionStorage.getItem(RECOVERY_MARKER_KEY);
    if (!raw) return;
    window.sessionStorage.removeItem(RECOVERY_MARKER_KEY);
    const marker = JSON.parse(raw) as Partial<RecoveryMarker>;
    Sentry.captureMessage('Stale-bundle chunk recovery reload', {
      level: 'info',
      tags: { stale_chunk_recovery: 'reload' },
      extra: { attempt: marker.attempt, reason: marker.reason, at: marker.at },
    });
  } catch {
    /* best-effort telemetry */
  }
}

/**
 * Drop-in replacement for `React.lazy` that recovers from stale-bundle chunk
 * failures by reloading. Use it for every dynamically-imported route/component.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() =>
    importer().catch((error) => {
      if (reloadForStaleChunk(error)) {
        // Hold the Suspense fallback while the page reloads. Reject after a
        // fallback delay so a blocked reload falls through to the ErrorBoundary
        // instead of stranding the user on an infinite spinner.
        return new Promise<{ default: T }>((_resolve, reject) => {
          window.setTimeout(() => reject(error), RELOAD_FALLBACK_MS);
        });
      }
      // Not a chunk error, or budget spent → let the ErrorBoundary render it.
      throw error;
    })
  );
}

/**
 * Call once at startup (index.tsx). Emits any pending recovery signal to Sentry
 * and installs a global safety net for dynamic imports that fail OUTSIDE a
 * React.lazy render path. Shares the same monotonic budget as `lazyWithReload`,
 * so it cannot loop either. (The cache-bust param is stripped separately, before
 * the router is built — see `absorbCacheBustParam` / routes.tsx.)
 */
export function installStaleChunkGuard(): void {
  if (typeof window === 'undefined' || guardInstalled) return;
  guardInstalled = true;
  reportPendingRecovery();
  window.addEventListener('unhandledrejection', (event) => {
    if (reloadForStaleChunk(event.reason)) {
      event.preventDefault();
    }
  });
}
